"use client"

import { useEffect } from "react"
import { create } from "zustand"
import { useSettingsStore } from "@/lib/settings-store"
import { useVRStore } from "@/lib/vr-store"
import { pickPreviewWinner, type PreviewCandidate } from "@/lib/force-preview"

/**
 * Force preview: with the setting on, the video post nearest the middle of the screen plays a muted, looping preview in the
 * grid, one at a time (see lib/force-preview.ts for the rule).
 *
 * Performance (CLAUDE.md "High-frequency state"): nothing here re-renders on scroll. A single timer measures the registered
 * cards four times a second and writes to the store ONLY when the winner changes, and each card subscribes to one boolean
 * ("am I the winner"), so a change re-renders at most two cards.
 */

const TICK_MS = 250

interface ForcePreviewState {
  winnerKey: string | null
  setWinner: (key: string | null) => void
}

const useForcePreviewStore = create<ForcePreviewState>((set, get) => ({
  winnerKey: null,
  setWinner: (key) => {
    if (get().winnerKey !== key) set({ winnerKey: key })
  },
}))

const registered = new Map<string, HTMLElement>()
let timer: ReturnType<typeof setInterval> | null = null

/** Reasons to play nothing right now, whatever the cards look like. */
function shouldPause(): boolean {
  if (typeof document === "undefined") return true
  if (document.hidden) return true
  if (useVRStore.getState().isVRMode) return true // the grid is under the VR player: invisible, so do not decode video for it
  if (document.querySelector('[role="dialog"][data-state="open"]')) return true // a post viewer or menu is open over the grid
  if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return true
  const connection = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection
  if (connection?.saveData) return true
  return false
}

function tick() {
  const { winnerKey, setWinner } = useForcePreviewStore.getState()
  if (registered.size === 0 || shouldPause()) {
    setWinner(null)
    return
  }
  const candidates: PreviewCandidate[] = []
  for (const [key, element] of registered) {
    const rect = element.getBoundingClientRect()
    candidates.push({ key, top: rect.top, bottom: rect.bottom })
  }
  setWinner(pickPreviewWinner(candidates, window.innerHeight, winnerKey))
}

function ensureTimer() {
  if (timer || registered.size === 0) return
  timer = setInterval(tick, TICK_MS)
  tick()
}

function stopTimerIfIdle() {
  if (registered.size > 0 || !timer) return
  clearInterval(timer)
  timer = null
  useForcePreviewStore.getState().setWinner(null)
}

/**
 * Registers a grid card as a force-preview candidate and says whether it is the one that should be playing.
 * `canPreview` is false for cards with nothing to play (images, or videos with no separate file), which then cost nothing.
 */
export function useForcePreview(key: string, element: React.RefObject<HTMLElement | null>, canPreview: boolean): boolean {
  const enabled = useSettingsStore((state) => state.forcePreview)
  const isWinner = useForcePreviewStore((state) => state.winnerKey === key)
  const active = enabled && canPreview

  useEffect(() => {
    const el = element.current
    if (!active || !el) return
    registered.set(key, el)
    ensureTimer()
    return () => {
      registered.delete(key)
      stopTimerIfIdle()
    }
  }, [active, key, element])

  return active && isWinner
}
